import { ICellRendererParams } from "ag-grid-community";
import { Box, Stack, Typography } from "@mui/joy";
import { AgentObj } from "@/types/interfaces/agents";
import CopyButton from "../../profile/components/CopyButton";

const isEmpty = (value: unknown) => value === undefined || value === null || value === "" || value === "-";

type Params = ICellRendererParams<AgentObj> & {
  /** The main line; the cell's (formatted) value when left out */
  primary?: (agent: AgentObj) => string | undefined;
  /** The quieter line underneath */
  secondary?: (agent: AgentObj) => string | undefined;
  /** A copy button for the main line, on row hover */
  copy?: boolean;
};

// Two related values in one cell, so a row reads like a person rather than a spreadsheet line
export const TwoLineCellRenderer = ({ data, value, valueFormatted, primary, secondary, copy }: Params) => {
  if (!data) return null;
  const main = primary ? primary(data) : valueFormatted ?? value;
  const sub = secondary?.(data);
  const mainEmpty = isEmpty(main);

  return (
    <Stack justifyContent="center" sx={{ minWidth: 0, height: "100%", lineHeight: 1.3 }}>
      <Stack direction="row" alignItems="center" gap={0.25} sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          level="body-sm"
          textColor={mainEmpty ? "text.tertiary" : "text.primary"}
          title={mainEmpty ? undefined : String(main)}
          sx={{ fontSize: "inherit" }}
        >
          {mainEmpty ? "–" : main}
        </Typography>
        {copy && !mainEmpty && (
          <Box className="copy-on-hover" sx={{ flexShrink: 0 }}>
            <CopyButton value={String(main)} />
          </Box>
        )}
      </Stack>
      {!isEmpty(sub) && (
        <Typography noWrap level="body-xs" textColor="text.tertiary" title={sub}>
          {sub}
        </Typography>
      )}
    </Stack>
  );
};
